import {
  AlertOctagon,
  AlertTriangle,
  BatteryCharging,
  Cpu,
  MapPin,
  MemoryStick,
  Signal,
} from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import type { DistrictHealthRow, HealthPayload } from "@busright/data-contract";
import { RAG, TrendChart } from "@busright/ui";

import { ChartCard } from "@/components/ChartCard";
import { Distribution, type DistributionBin } from "@/components/Distribution";
import { Drillable } from "@/components/Drillable";
import { EmptyState } from "@/components/EmptyState";
import { FhDistrictWorklist, isSyntheticDistrictName } from "@/components/FhDistrictWorklist";
import { FhMemoryFact } from "@/components/FhMemoryFact";
import { FhMoversList } from "@/components/FhMoversList";
import {
  FhSubscoreBreakdown,
  type FhSubscoreBreakdownSpec,
} from "@/components/FhSubscoreBreakdown";
import {
  InsightCallout,
  type InsightSeverity,
  type InsightStat,
} from "@/components/InsightCallout";
import { KpiCard } from "@/components/KpiCard";
import { MetricLabel } from "@/components/MetricTooltip";
import { PageHeader } from "@/components/PageHeader";
import { RiskDonut } from "@/components/RiskDonut";
import { useScope } from "@/context/ScopeContext";
import { useDistricts, useHealth, useMemory } from "@/hooks/queries";
import { formatInt, formatScore } from "@/lib/format";
import { weekdaysOnly } from "@/lib/trend";

// Fleet Health — flag → insight → action traceability. Every "N CRITICAL / N WARN" flag is
// Drillable straight into the device rows behind it (WHICH devices); each subscore explains WHY via
// its formula tooltip AND its own drill; the district worklist shows WHERE. Memory is reframed from
// the unexplained 0–100 "66" into a real used/total/free MB fact, with the subscore kept alongside
// for the composite math — both explainable now.

const MAX_MOVERS = 10;

const SUBSCORE_SPECS: readonly FhSubscoreBreakdownSpec[] = [
  { key: "gps", metricKey: "health.gps", label: "GPS", icon: MapPin, threshold: 80 },
  {
    key: "connectivity",
    metricKey: "health.connectivity",
    label: "Connectivity",
    icon: Signal,
    threshold: 85,
  },
  { key: "stability", metricKey: "health.stability", label: "Stability", icon: Cpu, threshold: 85 },
  {
    key: "battery",
    metricKey: "health.battery",
    label: "Battery",
    icon: BatteryCharging,
    threshold: 85,
  },
  {
    key: "memory",
    metricKey: "health.memory",
    label: "Memory (score)",
    icon: MemoryStick,
    threshold: 75,
  },
];

// The "so what" for Fleet Health: which subscore is the weakest link, how many sit below their WARN
// threshold, and which district carries the heaviest at-risk load. Computed from the payload — never
// hardcoded. Severity escalates with the live risk composition (CRIT → WARN → all-clear).
interface HealthInsight {
  readonly headline: string;
  readonly severity: InsightSeverity;
  readonly stats: readonly InsightStat[];
}

const worstDistrictLabel = (
  districts: readonly DistrictHealthRow[],
  nameById: ReadonlyMap<string, string>,
): string | null => {
  let worst: DistrictHealthRow | null = null;
  for (const d of districts) {
    if (isSyntheticDistrictName(nameById.get(d.districtId))) {
      continue;
    }
    const load = d.riskCounts.critical * 1000 + d.riskCounts.warn;
    const best = worst ? worst.riskCounts.critical * 1000 + worst.riskCounts.warn : -1;
    if (load > best) {
      worst = d;
    }
  }
  return worst && worst.riskCounts.critical + worst.riskCounts.warn > 0
    ? (nameById.get(worst.districtId) ?? worst.districtId)
    : null;
};

const buildHealthInsight = (
  payload: HealthPayload,
  nameById: ReadonlyMap<string, string>,
): HealthInsight => {
  // Weakest subscore = lowest average among the scored subscores.
  const scored = SUBSCORE_SPECS.map((s) => ({
    spec: s,
    value: payload.subscoreAverages[s.key],
  })).filter((x): x is { spec: FhSubscoreBreakdownSpec; value: number } => x.value !== null);
  const weakest = scored.reduce<{ spec: FhSubscoreBreakdownSpec; value: number } | null>(
    (acc, x) => (acc === null || x.value < acc.value ? x : acc),
    null,
  );
  const belowThreshold = scored.filter((x) => x.value < x.spec.threshold).length;
  const worst = worstDistrictLabel(payload.districts, nameById);
  const { critical, warn } = payload.riskCounts;
  const severity: InsightSeverity = critical > 0 ? "critical" : warn > 0 ? "warn" : "ok";

  const headline = weakest
    ? `${weakest.spec.label} is the weakest subscore (${formatScore(weakest.value)})${
        belowThreshold > 0
          ? ` — ${formatInt(belowThreshold)} of 5 subscores sit below their WARN threshold`
          : ""
      }${worst ? `; worst-loaded district is ${worst}.` : "."}`
    : "Subscores are not yet scored across the fleet.";

  const stats: InsightStat[] = [
    { label: "CRITICAL devices", value: formatInt(critical) },
    { label: "WARN devices", value: formatInt(warn) },
    {
      label: "weakest subscore",
      value: weakest ? `${weakest.spec.label} ${formatScore(weakest.value)}` : "—",
    },
  ];
  return { headline, severity, stats };
};

// Synthesize a coarse distribution from a subscore average so the small-multiple has shape. The
// contract exposes averages (not per-device rows) on this endpoint; bins approximate the spread and
// flag the low (at-risk) tail. Documented as illustrative until per-device rows ship.
const SPREAD = 12;
const BIN_DEFS = ["<60", "60-75", "75-85", "85-95", "95+"] as const;

const binsFromAverage = (avg: number | null): DistributionBin[] => {
  if (avg === null) {
    return BIN_DEFS.map((label) => ({ label, count: 0 }));
  }
  return BIN_DEFS.map((label, i) => {
    const center = [55, 67, 80, 90, 97][i];
    const dist = Math.abs(center - avg);
    const count = Math.max(0, Math.round(100 * Math.exp(-((dist / SPREAD) ** 2))));
    return { label, count, atRisk: i <= 1 };
  });
};

export const FleetHealthPage = (): ReactNode => {
  const { data, isLoading, isError, error } = useHealth();
  const districtsQuery = useDistricts();
  const memoryQuery = useMemory();
  const navigate = useNavigate();
  const { scope } = useScope();
  const meta = data?.meta;
  const payload = data?.data;
  const trend = payload ? weekdaysOnly(payload.healthTrend) : [];

  const districtRows = districtsQuery.data?.data.rows ?? [];
  const nameById = new Map(
    districtRows
      .filter((d): d is typeof d & { districtId: string } => d.districtId !== null)
      .map((d) => [d.districtId, d.accountName]),
  );

  const insight = payload ? buildHealthInsight(payload, nameById) : null;

  const districtsByRisk: readonly DistrictHealthRow[] = payload
    ? payload.districts
        .slice()
        .sort(
          (a, b) =>
            b.riskCounts.critical * 100 +
            b.riskCounts.warn -
            (a.riskCounts.critical * 100 + a.riskCounts.warn),
        )
    : [];

  if (isError) {
    return (
      <>
        <PageHeader title="Fleet Health" meta={meta} />
        <ChartCard title="Health">
          <EmptyState
            title="Couldn't load fleet health"
            message={error instanceof Error ? error.message : "Unknown error"}
            icon={AlertTriangle}
          />
        </ChartCard>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Fleet Health"
        description="Flag → insight → action: every CRITICAL/WARN count drills into the devices behind it, every subscore explains its own formula, and the worklist shows where the load sits."
        meta={meta}
      />

      {insight ? (
        <InsightCallout
          headline={insight.headline}
          severity={insight.severity}
          stats={insight.stats}
          cta={{ label: "Open Districts", to: "/districts" }}
        />
      ) : null}

      <div className="grid kpi-band">
        <Drillable
          metricKey="health.composite"
          scope={scope}
          label="Drill into CRITICAL devices — which devices, where, and why"
        >
          <KpiCard
            label={<MetricLabel metricKey="health.composite" label="CRITICAL devices" />}
            value={payload ? formatInt(payload.riskCounts.critical) : "—"}
            icon={AlertOctagon}
            source="rpt_hw_device_latest.risk_tier"
            loading={isLoading}
            valueColor={RAG.CRITICAL}
          />
        </Drillable>
        <Drillable
          metricKey="health.composite"
          scope={scope}
          label="Drill into WARN devices — which devices, where, and why"
        >
          <KpiCard
            label={<MetricLabel metricKey="health.composite" label="WARN devices" />}
            value={payload ? formatInt(payload.riskCounts.warn) : "—"}
            icon={AlertTriangle}
            source="rpt_hw_device_latest.risk_tier"
            loading={isLoading}
            valueColor={RAG.WARN}
          />
        </Drillable>
        <Drillable metricKey="fleet.criticalSharePct" scope={scope} label="Drill into % critical">
          <KpiCard
            label={<MetricLabel metricKey="fleet.criticalSharePct" />}
            value={
              payload
                ? `${((payload.riskCounts.critical / Math.max(1, payload.riskCounts.critical + payload.riskCounts.warn + payload.riskCounts.ok)) * 100).toFixed(1)}%`
                : "—"
            }
            icon={AlertTriangle}
            source="rpt_hw_device_latest.risk_tier"
            loading={isLoading}
          />
        </Drillable>
      </div>

      <div className="grid cols-3">
        <ChartCard
          title="Subscores — the WHY"
          subtitle="Fleet averages vs WARN threshold markers — click any subscore to see the devices behind it"
          source="rpt_hw_device_latest"
          loading={isLoading}
          className="span-2"
        >
          {payload ? (
            <FhSubscoreBreakdown
              specs={SUBSCORE_SPECS}
              averages={payload.subscoreAverages}
              scope={scope}
            />
          ) : (
            <EmptyState title="No subscores" />
          )}
        </ChartCard>

        <ChartCard
          title="Risk composition"
          subtitle="OK / WARN / CRIT"
          source="rpt_hw_device_latest"
          loading={isLoading}
        >
          {payload ? (
            <RiskDonut counts={payload.riskCounts} />
          ) : (
            <EmptyState title="No risk data" />
          )}
        </ChartCard>
      </div>

      <div style={{ marginTop: 16 }}>
        <ChartCard
          title="Memory — the fact behind the score"
          subtitle="Real used/total/free MB, not the 0–100 memory subscore above — this is what tells an operator whether to act"
          source="esper_device_status / esper_device_snapshot"
          loading={memoryQuery.isLoading}
        >
          <FhMemoryFact
            fleet={memoryQuery.data?.data.fleet ?? null}
            scope={scope}
            loading={memoryQuery.isLoading}
          />
        </ChartCard>
      </div>

      <div style={{ marginTop: 16 }}>
        <ChartCard
          title="Subscore distributions"
          subtitle="Illustrative spread from fleet averages — at-risk tail highlighted"
          source="rpt_hw_device_latest"
          loading={isLoading}
        >
          {payload ? (
            <div className="grid cols-5">
              {SUBSCORE_SPECS.map((s) => (
                <div key={s.key}>
                  <div className="card-subtitle" style={{ marginBottom: 6 }}>
                    {s.label} · {formatScore(payload.subscoreAverages[s.key])}
                  </div>
                  <Distribution
                    bins={binsFromAverage(payload.subscoreAverages[s.key])}
                    riskColor={RAG.CRITICAL}
                    height={110}
                  />
                </div>
              ))}
            </div>
          ) : (
            <EmptyState title="No distribution data" />
          )}
        </ChartCard>
      </div>

      <div className="grid cols-3" style={{ marginTop: 16 }}>
        <ChartCard
          title="Health trend"
          subtitle="Composite score · weekday-only"
          source="rpt_hw_device_latest"
          loading={isLoading}
          className="span-2"
        >
          {trend.length > 0 ? (
            <TrendChart data={trend} height={200} color={RAG.WARN} />
          ) : (
            <EmptyState title="No trend data" />
          )}
        </ChartCard>

        <ChartCard
          title="MoM movers"
          subtitle="Largest score declines — click a district to open its worklist"
          source="rpt_hw_district_latest"
          loading={isLoading}
        >
          {payload && payload.movers.length > 0 ? (
            <FhMoversList movers={payload.movers} max={MAX_MOVERS} />
          ) : (
            <EmptyState title="No movers" />
          )}
        </ChartCard>
      </div>

      <div style={{ marginTop: 16 }}>
        <ChartCard
          title="District worklist — the WHERE"
          subtitle="Sorted by at-risk load (CRIT, then WARN) — click a row to open Districts, or drill a row's device count for the devices behind it. Seed/test districts are filtered out."
          source="rpt_hw_district_latest ⋈ /api/districts"
          loading={isLoading || districtsQuery.isLoading}
        >
          <FhDistrictWorklist
            rows={districtsByRisk}
            districts={districtRows}
            onOpenDistricts={() => {
              void navigate("/districts");
            }}
          />
        </ChartCard>
      </div>
    </>
  );
};
